import React, { createContext, useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    addAnnotation,
    submitAnnotations,
    updateAnnotations,
    removeAnnotation,
    submitIsAnnotationReviewed,
    attachAssetToAnnotation,
    removeAnnotationAsset,
} from "store/features/annotations/annotations.actions";
import { getAnnotations, getDirtyStatus } from "store/features/annotations/annotations.selectors";
import { Annotation, AnnotationAction, AnnotationType } from "store/features/annotations/interfaces";
import { replaceArrayItem } from "utils";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { AssetType } from "api/assets";
import ReactPlayer from "react-player";

const INITIAL_ZOOM = 1;
const MAX_ZOOM = 2;
export const MIN_ZOOM = 0.2;
export const SCALE_FACTOR = 0.1;

interface AdMaterialAnnotationProviderProps {
    scale: number;
    annotations: Annotation[];
    isDrawingMode: boolean;
    annotationDrawingType: AnnotationType;
    selectedAnnotation?: Annotation;
    showAnnotations: boolean;
    showReviewed: boolean;
    validationError: boolean;
    isDirty: boolean;
    isPlaying: boolean;
    onZoomIn(): void;
    onZoomOut(): void;
    reset(): void;
    changeScale(newScale: number): void;
    onAddAnnotation(annotation: Annotation): void;
    onUpdateAnnotation(annotation: Annotation): void;
    onIsReviewedUpdate(annotation: Annotation): void;
    onRemoveAnnotation(annotation: Annotation): void;
    onAnnotationSelect(annotation: Annotation): void;
    onDrawSelectionHandler(type: AnnotationType): void;
    onSetShowAnnotations(): void;
    onSetShowReviewed(isVisible: boolean): void;
    onSaveAnnotations(action: AnnotationAction): void;
    onAttachAsset(selectionId: number, asset: File, assetType: AssetType): void;
    onRemoveAsset(assetId: number, selectionId: number): void;
    onSetVideoPlayer(playerRef: ReactPlayer | null): void;
    onSetIsPlaying(isPlaying: boolean): void;
}

export const AdMaterialAnnotationsContext = createContext({} as AdMaterialAnnotationProviderProps);

export const AdMaterialAnnotationsProvider: React.FC = ({ children }) => {
    const dispatch = useDispatch();
    const { selectedMaterialId, selectedMaterialSpecId, goBack, assetId, tab } = useMaterialSpecs();
    const [scale, setScale] = useState<number>(INITIAL_ZOOM);
    const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | undefined>();
    const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false);
    const [annotationDrawingType, setAnnotationDrawingType] = useState<AnnotationType>("STICKER");
    const [showAnnotations, setShowAnnotations] = useState<boolean>(false);
    const [showReviewed, setShowReviewed] = useState<boolean>(true);
    const [validationError, setValidationError] = useState<boolean>(false);
    const annotations = useSelector(getAnnotations);
    const isDirty = useSelector(getDirtyStatus);
    const player = useRef<ReactPlayer | null>(null); // video/audio
    const [isPlaying, setIsPlaying] = useState<boolean>(false); // video/audio

    useEffect(() => {
        if (validationError) {
            setValidationError(false);
        }
        //eslint-disable-next-line
    }, [selectedMaterialId]);

    useEffect(() => {
        setShowAnnotations(tab === "ANNOTATIONS");
    }, [tab]);

    const onZoomIn = useCallback(() => {
        setScale((currentScale) => {
            if (currentScale <= MAX_ZOOM) {
                currentScale += SCALE_FACTOR;

                return currentScale;
            }

            return currentScale;
        });
    }, []);

    const onZoomOut = useCallback(() => {
        setScale((currentScale) => {
            if (currentScale >= MIN_ZOOM) {
                currentScale -= SCALE_FACTOR;

                return currentScale;
            }

            return currentScale;
        });
    }, []);

    const onScaleChange = useCallback((newScale: number) => {
        setScale(newScale);
    }, []);

    const reset = useCallback(() => {
        setScale((currentScale) => {
            if (currentScale !== INITIAL_ZOOM) {
                return INITIAL_ZOOM;
            }

            return currentScale;
        });
    }, []);

    const onAddAnnotation = useCallback(
        (annotation: Annotation) => {
            dispatch(addAnnotation({ annotation }));
        },
        [dispatch]
    );

    const onUpdateAnnotation = useCallback(
        (annotation: Annotation) => {
            const index =
                annotation.id !== 0
                    ? annotations.findIndex(({ id }) => id === annotation.id)
                    : annotations.findIndex(({ selectionId }) => selectionId === annotation.selectionId);
            if (index > -1) {
                dispatch(
                    updateAnnotations({
                        theAnnotations: replaceArrayItem(annotations, index, annotation),
                    })
                );
            }
        },
        [annotations, dispatch]
    );

    const onIsReviewedUpdate = useCallback(
        (annotation: Annotation) => {
            // reviewed option is only available for saved annotations, so we don't have to check it
            const index = annotations.findIndex(({ id }) => id === annotation.id);
            if (index > -1) {
                const theAnnotations = replaceArrayItem<Annotation>(annotations, index, annotation);

                dispatch(updateAnnotations({ theAnnotations }));
                dispatch(submitIsAnnotationReviewed.request({ id: annotation.id, reviewed: annotation.reviewed }));
            }
        },
        [annotations, dispatch]
    );

    const onRemoveAnnotation = useCallback(
        (annotationToRemove: Annotation) => {
            const { id, selectionId } = annotationToRemove;
            const theAnnotations =
                id !== 0
                    ? annotations.filter((annotation) => annotation.id !== id)
                    : annotations.filter((annotation) => annotation.selectionId !== selectionId);

            dispatch(updateAnnotations({ theAnnotations }));

            // if 0, annotation is not yet saved in the database
            if (id !== 0) {
                dispatch(removeAnnotation.request({ annotationId: id }));
            }
        },
        [annotations, dispatch]
    );

    const onRemoveAsset = useCallback(
        (assetId: number, selectionId: number) => {
            dispatch(removeAnnotationAsset.request({ assetId, selectionId }));
        },
        [dispatch]
    );

    const onDrawSelectionHandler = (type: AnnotationType) => {
        if (isDrawingMode && type === annotationDrawingType) {
            setIsDrawingMode(false);
        } else {
            setIsDrawingMode(true);
            setAnnotationDrawingType(type);
            setShowAnnotations(true);
        }
    };

    const onAnnotationSelect = useCallback((annotation: Annotation) => {
        setSelectedAnnotation(annotation);
        if (player.current && annotation.timeStampSeconds !== undefined) {
            player.current.seekTo(annotation.timeStampSeconds, "seconds");
            setIsPlaying(false);
        }
    }, []);

    const onSetShowAnnotations = useCallback(() => {
        setShowAnnotations((visibility) => {
            const updatedValue = !visibility;

            if (!updatedValue) {
                setIsDrawingMode(false);
            }

            return updatedValue;
        });
    }, []);

    const onSetShowReviewed = useCallback((isVisible: boolean) => {
        setShowReviewed(isVisible);
    }, []);

    const onSetIsPlaying = useCallback((isPlaying: boolean) => {
        setIsPlaying(isPlaying);
    }, []);

    const onSaveAnnotations = useCallback(
        (action: AnnotationAction) => {
            // send only a list of updated annotations
            const modifiedAnnotations = annotations.filter((annotation) => annotation.modified);
            const emptyAnnotation = modifiedAnnotations.find((annotation) => annotation.comment === "");

            if (emptyAnnotation) {
                setValidationError(true);
                setSelectedAnnotation(emptyAnnotation);

                return;
            }
            if (validationError) {
                setValidationError(false);
            }

            if (selectedMaterialSpecId && selectedMaterialId && modifiedAnnotations.length > 0) {
                dispatch(
                    submitAnnotations.request({
                        materialSpecId: selectedMaterialSpecId,
                        adMaterialId: selectedMaterialId,
                        action,
                        annotations: modifiedAnnotations,
                        assetId,
                    })
                );

                goBack();
            }
        },
        [dispatch, selectedMaterialSpecId, selectedMaterialId, annotations, goBack, assetId, validationError]
    );

    const onAttachAsset = useCallback(
        (selectionId: number, asset: File, assetType: AssetType) => {
            if (selectedMaterialId && selectedMaterialSpecId) {
                dispatch(
                    attachAssetToAnnotation.request({
                        selectionId,
                        asset,
                        materialSpecificationId: selectedMaterialSpecId,
                        adMaterialId: selectedMaterialId,
                        assetType,
                    })
                );
            }
        },
        [dispatch, selectedMaterialSpecId, selectedMaterialId]
    );

    const onSetVideoPlayer = useCallback((playerRef: ReactPlayer | null) => {
        player.current = playerRef;
    }, []);

    return (
        <AdMaterialAnnotationsContext.Provider
            value={{
                scale,
                annotations,
                isDrawingMode,
                annotationDrawingType,
                selectedAnnotation,
                showAnnotations,
                showReviewed,
                validationError,
                isDirty,
                isPlaying,
                onZoomIn,
                onZoomOut,
                reset,
                onAddAnnotation,
                onUpdateAnnotation,
                onIsReviewedUpdate,
                onAnnotationSelect,
                onDrawSelectionHandler,
                onRemoveAnnotation,
                onSetShowAnnotations,
                onSaveAnnotations,
                onSetShowReviewed,
                onAttachAsset,
                onRemoveAsset,
                onSetVideoPlayer,
                onSetIsPlaying,
                changeScale: onScaleChange,
            }}>
            {children}
        </AdMaterialAnnotationsContext.Provider>
    );
};
